import { Tooltip } from 'antd';
import * as React from 'react';
import type { ResizeCallbackData } from 'react-resizable';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';
import Draggable from 'react-draggable';
import { useTheme } from 'src/theming/useTheme';

const useStyles = createUseStyles((theme: ThemeType) => ({
  tooltip: {
    '& .ant-tooltip-inner > div': {
      color: `${theme.color.semantic.text.inverse} !important`,
    },
    '& .ant-tooltip-inner > div > div': {
      color: `${theme.color.semantic.text.inverse} !important`,
    },
    '& .ant-tooltip-inner > div > div > div': {
      color: `${theme.color.semantic.text.inverse} !important`,
    },
    '& .ant-tooltip-inner > span': {
      color: `${theme.color.semantic.text.inverse} !important`,
    },
  },
}));

interface CustomTableTitleProps extends React.HTMLAttributes<HTMLTableCellElement> {
  onResize?: (e: React.SyntheticEvent<Element>, data: ResizeCallbackData) => void;
  onResizeStop?: (width: number) => void;
  width?: number;
  tableHeight?: number;
  resizable?: boolean;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  enableTextWrap?: boolean;
}

const CustomTableTitle: React.FC<CustomTableTitleProps> = (props) => {
  const {
    onResize,
    onResizeStop,
    resizable,
    enableTextWrap = false,
    children,
    width,
    tableHeight,
    style,
    ...restProps
  } = props;

  const classes = useStyles();
  const theme = useTheme();
  const childRef = React.useRef<HTMLDivElement>(null);

  const cellContent = (
    <div
      ref={childRef}
      style={{
        width: '100%',
        ...(enableTextWrap ? {
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          wordBreak: 'break-word',
          overflow: 'hidden',
        } : {
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        })
      }}
    >
      {children}
    </div>
  );

  const isTitleResizable = !(!width || !resizable);

  const handleDragStop = (e: any, data: any) => {
    e.preventDefault();
    e.stopPropagation();
    const draggingSpan = document.getElementById('__dragging_span_for_width_preview__');
    if (draggingSpan) {
      draggingSpan.remove();
    }
    const newWidth = (width || 0) + data.lastX + (data.lastX > 0 ? 5 : -5);
    setTimeout(() => {
      onResizeStop?.(newWidth);
    }, 0);
  };

  const handleDrag = (e: any, data: any) => {
    e.preventDefault();
    e.stopPropagation();
    const x = e.clientX;
    const y = e.clientY;
    if (childRef?.current) {
      const draggingSpan = childRef.current.parentElement?.querySelector(
        '.columm-resize-handler-dragging'
      );

      if (draggingSpan) {
        let spanElement = document.getElementById('__dragging_span_for_width_preview__');
        const tableElement = draggingSpan.closest('.ant-table-wrapper');
        const tableActualHeight = tableElement?.getBoundingClientRect().height ?? tableHeight;
        if (!spanElement) {
          spanElement = document.createElement('span');
          spanElement.style.left = `${x}px`;
          spanElement.style.top = `${(draggingSpan as HTMLElement).getBoundingClientRect().top ?? y}px`;
          spanElement.style.position = 'fixed';
          spanElement.style.width = '1px';
          spanElement.style.height = `${tableActualHeight}px`;
          spanElement.style.backgroundColor = theme.color.semantic.background.neutral.inverse;
          spanElement.id = '__dragging_span_for_width_preview__';
          document.body.appendChild(spanElement);
        } else {
          spanElement.style.left = `${x}px`;
        }
      }
    }
  };

  const renderResizeHandler = () => {
    return (
      <Draggable
        defaultClassName="columm-resize-handler"
        defaultClassNameDragging="columm-resize-handler-dragging"
        axis="x"
        onDrag={handleDrag}
        onStop={handleDragStop}
      >
        <span
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
      </Draggable>
    );
  };

  return (
    <th
      style={{
        ...(style || {}),
        width,
      }}
      {...restProps}
    >
      <Tooltip overlayClassName={classes.tooltip} title={children}>
        {cellContent}
      </Tooltip>

      {isTitleResizable ? renderResizeHandler() : null}
    </th>
  );
};

export default React.memo(CustomTableTitle);
